import { useState, useCallback, useEffect } from "react";
import { debounce } from "lodash";
import { MenuItem, CircularProgress, Avatar } from "@mui/material";
import { getApiData } from "../../../../utils/APIHelper";
import { getCurrentLocale } from "../../../../utils/Setting";
import CInput from "../../../../components/CInput";

const SearchLocation = ({ details, setDetails, errObj, setErrObj }) => {
  const [inputValue, setInputValue] = useState(details.location || "");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (details?.location) {
      setInputValue(details?.location);
    }
  }, [details.location]);

  const debouncedSearch = useCallback(
    debounce(async (value) => {
      if (value.length < 3) {
        setOptions([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setOptions([]);
      try {
        // Get current locale from environment
        const currentLocale = getCurrentLocale();
        const res = await getApiData(
          `api/project/location-list?input=${value}&locale=${currentLocale}`,
          "GET",
          {}
        );

        const users = res.locationList.predictions || [];

        // Format options
        const filteredOptions = users.map((user) => ({
          label: `${user.description}`,
          value: JSON.stringify(user),
        }));

        setOptions(filteredOptions.length ? filteredOptions : []);
      } catch (error) {
        console.error("Error fetching customer data:", error);
      } finally {
        setLoading(false);
      }
    }, 300),
    []
  );

  const handleSearch = (event) => {
    const value = event.target.value.trim();
    console.log("s", value);

    setInputValue(value);

    // If the input is empty, reset state and close dropdown
    if (!value) {
      setOpen(false);
      setOptions([]);
      setDetails((prev) => ({
        ...prev,
        location: "",
      }));
      return;
    }

    setOpen(true); // Open dropdown on valid search
    debouncedSearch(value);
  };

  const handleSelect = (optionValue) => {
    const selectedUser = JSON.parse(optionValue);
    setInputValue(selectedUser.description);
    setDetails((prev) => ({
      ...prev,
      location: selectedUser.description,
    }));
    setErrObj({
      ...errObj,
      locationErr: false,
      locationMsg: "",
    });
    setOpen(false); // Close dropdown after selection
  };

  return (
    <div style={{ display: "flex", gap: "10px" }}>
      <div style={{ position: "relative", width: "100%" }}>
        {/* Single Input Field (Search + Dropdown) */}
        <CInput
          label="Location"
          placeholder="Type to search location..."
          value={inputValue}
          onChange={handleSearch}
          onClick={() => setOpen(true)}
          error={errObj.locationErr}
          helpertext={errObj.locationMsg}
          autoComplete="off"
        />

        {/* Dropdown Menu */}
        {open && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              zIndex: 1000,
              background: "white",
              border: options.length > 0 && "1px solid #ccc",
              borderRadius: "4px",
              boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
              maxHeight: "200px",
              overflowY: "auto",
              scrollbarWidth: "none",
            }}
          >
            {loading ? (
              <MenuItem disabled>
                <CircularProgress size={20} />
                &nbsp; Searching...
              </MenuItem>
            ) : (
              options.map((option) => {
                const user = JSON.parse(option.value);
                return (
                  <MenuItem
                    key={user.id || option.label}
                    onClick={() => handleSelect(option.value)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    {option.label}
                  </MenuItem>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchLocation;
